import React, {createContext, useContext, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {FormattedMessage} from 'react-intl';
import {AppDispatch, RootState} from '../../../../store/configureStore';
import {
  showGlobalMessage,
  hideGlobalMessage
} from '../../../redux/actions/globalMessageActions';

export type GlobalMessageContext = {
  open: (message: GlobalMessage, cb?: () => void) => void,
  close: () => void,
  message: GlobalMessage,
  isOpen: boolean,
}

const defaultMessage =
  <FormattedMessage
    id={'message-contact-support'}
    defaultMessage={'Something went wrong. Please contact support.'}
  />;

export interface GlobalMessageContextProviderProps {
  children: React.ReactNode;
}

export const defaultContext: GlobalMessageContext = {
  open: (message: GlobalMessage) => message,
  close: () => null,
  message: defaultMessage,
  isOpen: false,
};

export const GlobalMessageContext = createContext<GlobalMessageContext>(defaultContext);
GlobalMessageContext.displayName = 'GlobalMessageContext';

export const useGlobalMessageContext = () => useContext(GlobalMessageContext);

export const GlobalMessageContextProvider = ({children}: GlobalMessageContextProviderProps) => {
  const message = useSelector((state: RootState) => state.globalMessage.message);
  const isOpen = useSelector((state: RootState) => state.globalMessage.globalMessageIsOpen);
  const [onCloseGlobalMessage, setOnCloseGlobalMessage] = useState<(() => void) | null>(null);
  const dispatch: AppDispatch = useDispatch();

  const openGlobalMessage = (message: GlobalMessage, cb?: () => void) => {
    dispatch(showGlobalMessage(message));
    if (cb) {
      setOnCloseGlobalMessage(() => cb);
    }
  };

  const closeGlobalMessage = () => {
    dispatch(hideGlobalMessage());
    if (onCloseGlobalMessage) {
      const callback = onCloseGlobalMessage;
      setOnCloseGlobalMessage(() => null);
      callback();
    }
  };

  const context: GlobalMessageContext = {
    ...defaultContext,
    open: openGlobalMessage,
    close: closeGlobalMessage,
    isOpen,
    message,
  };

  return (
    <GlobalMessageContext.Provider value={context}>
      {children}
    </GlobalMessageContext.Provider>
  );
};
